import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FileText, TrendingUp, TrendingDown, Minus, AlertTriangle, Calendar, User, Clock } from 'lucide-react';

// Mock report data for the public doctor view
const mockReport = {
  patientName: 'Sarah Johnson',
  reportPeriod: '1 Feb 2025 – 28 Feb 2025',
  generatedAt: '28 Feb 2025, 09:14',
  totalEntries: 18,
  primarySymptom: {
    name: 'Persistent Headache',
    location: 'Frontal / Temporal',
    avgSeverity: 7,
    trend: 'increasing',
    frequency: 'Daily for 14 days',
    impactLevel: 'High',
  },
  triggers: ['Stress', 'Poor sleep', 'Prolonged screen time'],
  impacts: [
    { area: 'Sleep', score: 7 },
    { area: 'Concentration / Work', score: 6 },
    { area: 'Mobility', score: 3 },
    { area: 'Social Activities', score: 4 },
  ],
  timeline: [
    { date: '28 Feb', severity: 7, symptoms: 'Headache', note: 'Throbbing, lasted 4 hours' },
    { date: '26 Feb', severity: 6, symptoms: 'Headache', note: 'Afternoon onset after screen use' },
    { date: '24 Feb', severity: 8, symptoms: 'Headache, Nausea', note: 'Worst episode — nausea alongside headache' },
    { date: '22 Feb', severity: 5, symptoms: 'Headache', note: 'Mild, resolved after rest' },
    { date: '20 Feb', severity: 6, symptoms: 'Headache', note: 'Morning headache, stressful day at work' },
  ],
  patientNotes: 'Headaches are worst in the morning and after long screen sessions. Paracetamol provides temporary relief but symptoms return. Concerned about increasing frequency over the past two weeks. No visual disturbances noted.',
};

const SeverityBadge = ({ value }) => {
  const cls = value >= 7 ? 'bg-red-100 text-red-700 border-red-200' : value >= 5 ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200';
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${cls}`}>{value}/10</span>;
};

const TrendIndicator = ({ trend }) => {
  if (trend === 'increasing') return <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-sm"><TrendingUp className="w-4 h-4" /> Increasing</span>;
  if (trend === 'decreasing') return <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm"><TrendingDown className="w-4 h-4" /> Decreasing</span>;
  return <span className="inline-flex items-center gap-1 text-amber-600 font-semibold text-sm"><Minus className="w-4 h-4" /> Stable</span>;
};

const SeverityBar = ({ value }) => {
  const color = value >= 7 ? 'bg-red-400' : value >= 5 ? 'bg-amber-400' : 'bg-green-400';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${value * 10}%` }} />
      </div>
      <span className="text-xs font-bold text-gray-600 w-8 text-right">{value}/10</span>
    </div>
  );
};

const DoctorReportView = () => {
  const [report, setReport] = useState(null);

  useEffect(() => {
    // 1. Try URL query parameter first (works in any new tab/session — used by email & QR links)
    try {
      const params = new URLSearchParams(window.location.search);
      const encoded = params?.get('report');
      if (encoded) {
        const parsed = JSON.parse(decodeURIComponent(encoded));
        if (parsed && parsed?.patientName && parsed?.patientName !== 'Patient') {
          setReport(parsed);
          return;
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    // 2. Fall back to sessionStorage (same-tab navigation)
    try {
      const stored = sessionStorage.getItem('doctorReportData');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed?.patientName && parsed?.patientName !== 'Patient') {
          setReport(parsed);
          return;
        }
      }
    } catch (e) {
      // ignore parse errors
    }
    // 3. Final fallback to mock data
    setReport(mockReport);
  }, []);

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Patient Consultation Summary — MyBodyToday</title>
        <meta name="description" content="Patient-generated symptom summary for clinical review" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 print:bg-white">
        {/* Clinical Header Bar */}
        <div className="bg-white border-b border-gray-200 print:border-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">MyBodyToday™</p>
                  <p className="text-xs text-gray-500">Patient Consultation Summary</p>
                </div>
              </div>
              <button
                onClick={() => window.print()}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors print:hidden">
                Print / Save PDF
              </button>
            </div>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 print:px-0 print:py-4">
          {/* Report Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden print:shadow-none print:border-0">
            {/* Report Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 sm:px-8 py-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-white font-bold text-xl sm:text-2xl">Patient Consultation Summary</h1>
                  <p className="text-green-100 text-sm mt-1">For clinical review — generated by patient via MyBodyToday</p>
                </div>
                <div className="flex flex-col gap-1 sm:text-right">
                  <div className="flex items-center gap-1.5 text-green-100 text-xs">
                    <Clock className="w-3.5 h-3.5" />
                    Generated: {report?.generatedAt}
                  </div>
                  <div className="flex items-center gap-1.5 text-green-100 text-xs">
                    <Calendar className="w-3.5 h-3.5" />
                    Period: {report?.reportPeriod}
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 sm:px-8 py-6 space-y-6">
              {/* Patient Info */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Patient Name</p>
                    <p className="font-bold text-gray-900">{report?.patientName}</p>
                  </div>
                </div>
                <div className="sm:ml-auto flex gap-6">
                  <div>
                    <p className="text-xs text-gray-500">Total Entries</p>
                    <p className="font-bold text-gray-900">{report?.totalEntries}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Report Period</p>
                    <p className="font-bold text-gray-900">{report?.reportPeriod}</p>
                  </div>
                </div>
              </div>

              {/* Primary Symptom */}
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Primary Symptom</h2>
                <div className="bg-red-50 border border-red-100 rounded-xl p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                      <p className="text-xl font-bold text-gray-900">{report?.primarySymptom?.name}</p>
                      <p className="text-sm text-gray-500 mt-0.5">Location: {report?.primarySymptom?.location}</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Avg Severity</p>
                        <SeverityBadge value={report?.primarySymptom?.avgSeverity} />
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Trend</p>
                        <TrendIndicator trend={report?.primarySymptom?.trend} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-red-100 grid sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500">Frequency</p>
                      <p className="text-sm font-semibold text-gray-800">{report?.primarySymptom?.frequency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Impact Level</p>
                      <p className="text-sm font-semibold text-red-700">{report?.primarySymptom?.impactLevel}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Possible Triggers */}
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Possible Triggers</h2>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                  <div className="flex flex-wrap gap-2">
                    {report?.triggers?.map((t) => (
                      <span key={t} className="text-sm bg-white text-amber-800 border border-amber-200 px-3 py-1.5 rounded-lg font-medium">{t}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Impact on Daily Life */}
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Impact on Daily Life</h2>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div className="space-y-3">
                    {report?.impacts?.map((impact, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <span className="text-sm text-gray-700 w-40 flex-shrink-0">{impact?.area}</span>
                        <div className="flex-1">
                          <SeverityBar value={impact?.score} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Symptom Timeline */}
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Symptom Timeline</h2>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Symptoms</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report?.timeline?.map((entry, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-700 whitespace-nowrap">{entry?.date}</td>
                          <td className="px-4 py-3 text-gray-700">{entry?.symptoms}</td>
                          <td className="px-4 py-3"><SeverityBadge value={entry?.severity} /></td>
                          <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{entry?.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Patient Notes */}
              <div>
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Patient Notes</h2>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-sm text-gray-700 leading-relaxed italic">"{report?.patientNotes}"</p>
                </div>
              </div>

              {/* Safety Disclaimer */}
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-5">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-amber-800 mb-1">Safety Disclaimer</p>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    This information is patient-generated and not medical advice. All data is self-reported via the MyBodyToday app.
                    Please consult a qualified healthcare professional for diagnosis and treatment.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center pt-2 pb-1">
                <p className="text-xs text-gray-400">Generated by MyBodyToday™ · mybodytoda1849.builtwithrocket.new</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default DoctorReportView;
